<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, computed, reactive } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { Grid, type GridSortChangeEvent, type GridPageChangeEvent } from '@progress/kendo-vue-grid'
import type { SortDescriptor } from '@progress/kendo-data-query'
import { orderBy } from '@progress/kendo-data-query'
import { formatDate } from "../../api/Common.ts"

import { PwsLeadGetList, PwsLeadAccept, PwsLeadReject, PwsLeadRefer, PwsLeadReferToGetList } from '../../api/Lead.ts'
import { handleError } from '../../services/ErrorService.ts'
import type { ILeadItem, IPwsLeadReferToGetListResponse } from '../../types/LeadTypes.ts'
import Swal from 'sweetalert2'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

import { Button as KButton } from '@progress/kendo-vue-buttons'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import { SvgIcon } from '@progress/kendo-vue-common'
import { moreVerticalIcon, filterIcon, checkIcon, xIcon as closeIcon, shareIcon } from '@progress/kendo-svg-icons'
import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs'
import { DropDownList } from '@progress/kendo-vue-dropdowns'

const logger = useLogger()
setStoreLogger(logger)

// Grid state
const skip = ref(0)
const take = ref(20)
const loader = ref<boolean>(false)
const sort = ref<SortDescriptor[]>([{ field: 'createDate', dir: 'desc' }])
const gridData = ref<ILeadItem[]>([])
const searchQuery = ref('')

// Dialog state
const dialogVisible = ref(false)
const dialogType = ref<'accept' | 'reject' | 'refer'>('accept')
const selectedLead = ref<ILeadItem | null>(null)
const consultantList = ref<any[]>([])
const selectedConsultant = ref<any>(null)
const loadingConsultants = ref(false)

// Computed properties for grid data
const sortedGridData = computed<ILeadItem[]>(() => {
  let data = gridData.value

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    data = data.filter((item: ILeadItem) => {
      return (
        item.firstName?.toLowerCase().includes(query) ||
        item.lastName?.toLowerCase().includes(query) ||
        item.emailAddress?.toLowerCase().includes(query) ||
        item.primaryPhone?.includes(query)
      )
    })
  }


  return orderBy(data, sort.value)
  //return sortedData
})

const pageSortedGridData = computed(() => sortedGridData.value.slice(skip.value, take.value + skip.value))
const total = computed(() => sortedGridData.value.length)

// Helper function to get the lead GUID as a string
// Prefer sfaLeadMstGuidString if available, otherwise fall back to sfaLeadMstGuid
function getLeadGuid(lead: any): string {
  if (lead?.sfaLeadMstGuidString !== null && lead?.sfaLeadMstGuidString !== undefined) {
    return lead.sfaLeadMstGuidString;
  } else if (lead?.sfaLeadMstGuid !== null && lead?.sfaLeadMstGuid !== undefined) {
    return String(lead.sfaLeadMstGuid);
  }
  return '';
}

// Function to determine lead status based on dates
function getLeadStatus(item: ILeadItem): string {
  if (item.acceptedDate) {
    return t('consultantCorner.leads.Accepted')
  } else if (item.closedDate) {
    return t('consultantCorner.leads.Closed')
  } else {
    return t('consultantCorner.leads.Open')
  }
}

// Grid columns
const columns = ref([
  {
    field: 'firstName',
    title: t('consultantCorner.leads.FirstName'),
    width: resolveColumnWidth('180px', t('consultantCorner.leads.FirstName')),
    sortable: true
  },
  {
    field: 'lastName',
    title: t('consultantCorner.leads.LastName'),
    width: resolveColumnWidth('180px', t('consultantCorner.leads.LastName')),
    sortable: true
  },
  {
    field: 'emailAddress',
    title: t('consultantCorner.leads.Email'),
    width: resolveColumnWidth('240px', t('consultantCorner.leads.Email')),
    sortable: true
  },
  {
    field: 'primaryPhone',
    title: t('consultantCorner.leads.Phone'),
    width: resolveColumnWidth('120px', t('consultantCorner.leads.Phone')),
    sortable: true
  },
  {
    field: 'createDate',
    title: t('consultantCorner.leads.CreateDate'),
    width: resolveColumnWidth('120px', t('consultantCorner.leads.CreateDate')),
    cell: 'dateTemplate',
    sortable: true
  },
  {
    field: 'status',
    title: t('consultantCorner.leads.Status'),
    width: resolveColumnWidth('120px', t('consultantCorner.leads.Status')),
    cell: 'statusTemplate',
    sortable: true
  },
  {
    field: 'leadDistSource',
    title: t('consultantCorner.leads.Source'),
    width: resolveColumnWidth('120px', t('consultantCorner.leads.Source')),
    sortable: true
  },
  {
    field: 'actions',
    title: t('consultantCorner.leads.Actions'),
    width: resolveColumnWidth('180px', t('consultantCorner.leads.Actions')),
    cell: 'actionsTemplate',
    sortable: false // Action columns typically shouldn't be sortable
  }
])

// Event handlers
function pageChangeHandler(event: GridPageChangeEvent) {
  loader.value = true
  setTimeout(() => {
    loader.value = false
    skip.value = event.page.skip
    take.value = event.page.take
  }, 300)
}

function sortChangeHandler(e: GridSortChangeEvent) {
  loader.value = true
  if (e.sort[0] !== undefined) {
    sort.value = e.sort
  }
  loader.value = false
}

// Search handler
function onSearchInput() {
  // The computed property will automatically update when searchQuery changes
}

// Load data
async function loadLeads() {
  loader.value = true
  try {
    const response = await PwsLeadGetList()

    if (response && response.items) {
      // Assign the response items directly to gridData
      gridData.value = response.items
      console.log('Loaded leads:', JSON.stringify(gridData.value))
    }
  } catch (error) {
    await handleError(error, t)
  } finally {
    loader.value = false
  }
}

// Action handlers
async function handleAcceptLead(lead: ILeadItem) {
  console.log('Selected lead for accept:', JSON.stringify(lead))
  console.log('Lead GUID:', getLeadGuid(lead))

  // Find the original lead object from gridData using the GUID
  const originalLead = gridData.value.find(item => getLeadGuid(item) === getLeadGuid(lead))

  if (originalLead) {
    console.log('Found original lead:', JSON.stringify(originalLead))
    console.log('Original lead GUID:', getLeadGuid(originalLead))
    console.log('Original lead GUID type:', typeof getLeadGuid(originalLead))
    selectedLead.value = originalLead
  } else {
    console.warn('Could not find original lead in gridData, using provided lead')
    selectedLead.value = lead
  }

  dialogType.value = 'accept'
  dialogVisible.value = true
}

async function handleRejectLead(lead: ILeadItem) {
  // Find the original lead object from gridData using the GUID
  const originalLead = gridData.value.find(item => getLeadGuid(item) === getLeadGuid(lead))

  if (originalLead) {
    console.log('Found original lead for reject:', JSON.stringify(originalLead))
    console.log('Original lead GUID:', getLeadGuid(originalLead))
    selectedLead.value = originalLead
  } else {
    console.warn('Could not find original lead in gridData for reject, using provided lead')
    selectedLead.value = lead
  }

  dialogType.value = 'reject'
  dialogVisible.value = true
}

async function handleReferLead(lead: ILeadItem) {
  // Find the original lead object from gridData using the GUID
  const originalLead = gridData.value.find(item => getLeadGuid(item) === getLeadGuid(lead))

  if (originalLead) {
    console.log('Found original lead for refer:', JSON.stringify(originalLead))
    console.log('Original lead GUID:', getLeadGuid(originalLead))
    selectedLead.value = originalLead
  } else {
    console.warn('Could not find original lead in gridData for refer, using provided lead')
    selectedLead.value = lead
  }

  dialogType.value = 'refer'
  dialogVisible.value = true
  await loadConsultants()
}

async function loadConsultants() {
  loadingConsultants.value = true
  try {
    const response = await PwsLeadReferToGetList()
    if (response && response.items) {
      consultantList.value = response.items
      if (consultantList.value.length > 0) {
        selectedConsultant.value = consultantList.value[0]
      }
    }
  } catch (error) {
    await handleError(error, t)
  } finally {
    loadingConsultants.value = false
  }
}

async function confirmAction() {
  if (!selectedLead.value) return

  try {
    // Get the GUID from the lead as a string
    const leadGuid = getLeadGuid(selectedLead.value)

    console.log('Using GUID for API call:', leadGuid);
    console.log('GUID type:', typeof leadGuid);

    if (dialogType.value === 'accept') {
      console.log('Confirming accept for lead:', JSON.stringify(selectedLead.value))

      try {
        const response = await PwsLeadAccept(leadGuid)
        console.log('Accept response:', response)
        Swal.fire({
          title: t('alerts.Success'),
          text: t('consultantCorner.leads.LeadAccepted'),
          icon: 'success'
        })
      } catch (acceptError: any) {
        console.error('Accept error details:', acceptError)
        console.error('Accept error response:', acceptError.response?.data)
        throw acceptError
      }
    } else if (dialogType.value === 'reject') {
      console.log('Confirming reject for lead:', JSON.stringify(selectedLead.value))

      try {
        const response = await PwsLeadReject(leadGuid)
        console.log('Reject response:', response)
        Swal.fire({
          title: t('alerts.Success'),
          text: t('consultantCorner.leads.LeadRejected'),
          icon: 'success'
        })
      } catch (rejectError: any) {
        console.error('Reject error details:', rejectError)
        console.error('Reject error response:', rejectError.response?.data)
        throw rejectError
      }
    } else if (dialogType.value === 'refer' && selectedConsultant.value) {
      console.log('Confirming refer for lead:', JSON.stringify(selectedLead.value))
      const referBeeNumber = selectedConsultant.value.beeNumber?.beeNumber;
      console.log('Consultant Bee Number:', referBeeNumber)

      try {
        const response = await PwsLeadRefer(leadGuid, referBeeNumber)
        console.log('Refer response:', response)
        Swal.fire({
          title: t('alerts.Success'),
          text: t('consultantCorner.leads.LeadReferred'),
          icon: 'success'
        })
      } catch (referError: any) {
        console.error('Refer error details:', referError)
        console.error('Refer error response:', referError.response?.data)
        throw referError
      }
    }

    // Reload leads after action
    await loadLeads()
    closeDialog()
  } catch (error) {
    console.error('Action error:', error)
    await handleError(error, t)
  }
}

function closeDialog() {
  dialogVisible.value = false
  selectedLead.value = null
  selectedConsultant.value = null
}

// Mobile pagination handlers
function handleMobilePagination(newSkip: number) {
  skip.value = newSkip

  // Create a mock event object
  const mockEvent = {
    page: { skip: newSkip, take: take.value },
    event: {
      preventDefault: () => {},
      stopPropagation: () => {},
      target: document.createElement('button')
    }
  } as GridPageChangeEvent

  pageChangeHandler(mockEvent)
}

// Initialize component
onMounted(async () => {
  logger.debug('onMounted AoeLeadList.vue')
  await loadLeads()
})
</script>

<template>
  <div>
    <div class="pageHeader pb-3 me-3">{{ t('consultantCorner.leads.MyLeads') }}</div>
    <div class="headerborder w-100">
      <div class="row p-2 align-items-center">
        <div class="d-flex align-items-center">
          <span class="pageHeader me-3">{{ t('consultantCorner.leads.AllLeads') }}</span>
          <div class="count-pill">{{ t('consultantCorner.leads.leadCount', { count: pageSortedGridData.length }) }}</div>
        </div>
        <div class="pageSubHeader">{{ t('consultantCorner.leads.LeadSubHeader') }}</div>
      </div>
      <div class="mt-3 py-2 d-flex justify-content-end flex-wrap w-100">
        <div class="search-container pl-2">
          <KTextBox
            v-model="searchQuery"
            @input="onSearchInput"
            :placeholder="t('consultantCorner.leads.searchPlaceholder')"
            class="search-input"
          />
        </div>
      </div>
    </div>

    <div class="aoeleads w-100 mx-0">
      <div class="aoeleads">
        <!-- Desktop Grid View -->
        <div class="d-none d-md-block">
          <Grid
            :data-items="pageSortedGridData"
            :resizable="true"
            :sortable="true"
            :sort="sort"
            :pageable="true"
            :skip="skip"
            :take="take"
            :total="total"
            :columns="columns"
            @sortchange="sortChangeHandler"
            @pagechange="pageChangeHandler"
            :loader="loader"
          >
            <template v-slot:dateTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-center">
                {{ formatDate(props.dataItem.createDate) }}
              </td>
            </template>
            <template v-slot:statusTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-center">
                {{ getLeadStatus(props.dataItem) }}
              </td>
            </template>
            <template v-slot:actionsTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-center action-buttons mt-2">
                <KButton
                  :title="t('consultantCorner.leads.Accept')"
                  class="action-btn accept-btn"
                  @click="handleAcceptLead(props.dataItem)"
                  :fill-mode="'flat'"
                  :size="'small'"
                  :disabled="!!props.dataItem.acceptedDate"
                  :class="{ 'disabled-btn': props.dataItem.acceptedDate }"
                >
                  <SvgIcon :icon="checkIcon" />
                </KButton>
                <KButton
                  :title="t('consultantCorner.leads.Reject')"
                  class="action-btn reject-btn"
                  @click="handleRejectLead(props.dataItem)"
                  :fill-mode="'flat'"
                  :size="'small'"
                >
                  <SvgIcon :icon="closeIcon" />
                </KButton>
                <KButton
                  :title="t('consultantCorner.leads.Refer')"
                  class="action-btn refer-btn"
                  @click="handleReferLead(props.dataItem)"
                  :fill-mode="'flat'"
                  :size="'small'"
                >
                  <SvgIcon :icon="shareIcon" />
                </KButton>
              </td>
            </template>
          </Grid>
        </div>

        <!-- Mobile Card View -->
        <div class="d-block d-md-none mobile-card-view">
          <div v-if="loader" class="mobile-loader">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('consultantCorner.leads.Loading') }}</span>
            </div>
          </div>
          <div v-else>
            <!-- Pagination Controls Top -->
            <div class="mobile-pagination d-flex justify-content-between align-items-center mb-3">
              <div class="page-info">
                {{ t('orderList.Page') }} {{ skip / take + 1 }} {{ t('orderList.Of') }} {{ Math.ceil(total / take) }}
              </div>
              <div class="pagination-controls">
                <KButton
                  :disabled="skip === 0"
                  @click="handleMobilePagination(Math.max(0, skip - take))"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &lt;
                </KButton>
                <KButton
                  :disabled="skip + take >= total"
                  @click="handleMobilePagination(skip + take)"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &gt;
                </KButton>
              </div>
            </div>

            <!-- Cards -->
            <div class="mobile-cards">
              <div v-for="(item, index) in pageSortedGridData" :key="index" class="mobile-card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <span class="lead-name">{{ item.firstName }} {{ item.lastName }}</span>
                </div>

                <div class="card-body">
                  <div class="card-row">
                    <div class="card-label">{{ t('consultantCorner.leads.Email') }}:</div>
                    <div class="card-value">{{ item.emailAddress }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('consultantCorner.leads.Phone') }}:</div>
                    <div class="card-value">{{ item.primaryPhone }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('consultantCorner.leads.CreateDate') }}:</div>
                    <div class="card-value">{{ formatDate(item.createDate) }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('consultantCorner.leads.Status') }}:</div>
                    <div class="card-value">{{ getLeadStatus(item) }}</div>
                  </div>

                  <div class="card-row">
                    <div class="card-label">{{ t('consultantCorner.leads.Source') }}:</div>
                    <div class="card-value">{{ item.leadDistSource }}</div>
                  </div>

                  <div class="card-actions mt-3">
                    <KButton
                      :title="t('consultantCorner.leads.Accept')"
                      class="action-btn accept-btn"
                      @click="handleAcceptLead(item)"
                      :fill-mode="'flat'"
                      :disabled="!!item.acceptedDate"
                      :class="{ 'disabled-btn': item.acceptedDate }"
                    >
                      <SvgIcon :icon="checkIcon" />
                      {{ t('consultantCorner.leads.Accept') }}
                    </KButton>
                    <KButton
                      :title="t('consultantCorner.leads.Reject')"
                      class="action-btn reject-btn"
                      @click="handleRejectLead(item)"
                      :fill-mode="'flat'"
                    >
                      <SvgIcon :icon="closeIcon" />
                      {{ t('consultantCorner.leads.Reject') }}
                    </KButton>
                    <KButton
                      :title="t('consultantCorner.leads.Refer')"
                      class="action-btn refer-btn"
                      @click="handleReferLead(item)"
                      :fill-mode="'flat'"
                    >
                      <SvgIcon :icon="shareIcon" />
                      {{ t('consultantCorner.leads.Refer') }}
                    </KButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination Controls Bottom -->
            <div class="mobile-pagination d-flex justify-content-between align-items-center mt-3">
              <div class="page-info">
                {{ t('orderList.Page') }} {{ skip / take + 1 }} {{ t('orderList.Of') }} {{ Math.ceil(total / take) }}
              </div>
              <div class="pagination-controls">
                <KButton
                  :disabled="skip === 0"
                  @click="handleMobilePagination(Math.max(0, skip - take))"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &lt;
                </KButton>
                <KButton
                  :disabled="skip + take >= total"
                  @click="handleMobilePagination(skip + take)"
                  :fill-mode="'clear'"
                  class="page-btn"
                >
                  &gt;
                </KButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Confirmation Dialogs -->
    <Dialog
      v-if="dialogVisible"
      :title="dialogType === 'accept' ? t('consultantCorner.leads.Accept') :
              dialogType === 'reject' ? t('consultantCorner.leads.Reject') :
              t('consultantCorner.leads.Refer')"
      @close="closeDialog"
      :width="400"
      class="centered-dialog"
    >
      <div class="dialog-content">
        <p v-if="dialogType === 'accept'">
          {{ t('consultantCorner.leads.ConfirmAccept') }}
        </p>
        <p v-else-if="dialogType === 'reject'">
          {{ t('consultantCorner.leads.ConfirmReject') }}
        </p>
        <div v-else-if="dialogType === 'refer'">
          <p>{{ t('consultantCorner.leads.ConfirmRefer') }}</p>
          <div class="consultant-select mt-3">
            <label>{{ t('consultantCorner.leads.ReferTo') }}</label>
            <div v-if="loadingConsultants" class="loading-consultants">
              {{ t('consultantCorner.leads.Loading') }}
            </div>
            <div v-else-if="consultantList.length === 0" class="no-consultants">
              {{ t('consultantCorner.leads.NoConsultantsFound') }}
            </div>
            <DropDownList
              v-else
              :data-items="consultantList"
              textField="firstName"
              valueField="beeNumber.beeNumber"
              v-model="selectedConsultant"
              :placeholder="t('consultantCorner.leads.SelectConsultant')"
            />
          </div>
        </div>
      </div>
      <DialogActionsBar>
        <KButton
          @click="closeDialog"
          :fill-mode="'flat'"
        >
          {{ t('consultantCorner.leads.Cancel') }}
        </KButton>
        <KButton
          @click="confirmAction"
          :primary="true"
          :disabled="dialogType === 'refer' && (!selectedConsultant || loadingConsultants)"
        >
          {{ t('consultantCorner.leads.Confirm') }}
        </KButton>
      </DialogActionsBar>
    </Dialog>
  </div>
</template>

<style lang="scss" scoped>
.count-pill {
  background-color: var(--vt-c-teal-dark);
  border-radius: $radius-xl;
  @include typography-body;
  padding: $spacing-1 $spacing-3;
}

.search-input {
  min-width: 250px;
}

/* Mobile card view styling */
.mobile-card-view {
  width: 100%;
}

.mobile-loader {
  display: flex;
}

.disabled-btn {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.mobile-card {
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  background-color: var(--vt-c-white);
}

.mobile-card .card-header {
  padding: 0.75rem 1rem;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #e0e0e0;
  border-radius: $radius-md $radius-md 0 0;
}

.mobile-card .lead-name {
  @include typography-body-large;
  @include font-weight-semibold;
}

.mobile-card .card-body {
  padding: 1rem;
}

.mobile-card .card-row {
  display: flex;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.mobile-card .card-label {
  @include font-weight-medium;
  width: 100px;
  flex-shrink: 0;
}

.mobile-card .card-value {
  flex: 1;
}

.mobile-pagination {
  padding: 0.5rem 0;
}

.mobile-pagination .page-btn {
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Action buttons styling */
.action-buttons {
  white-space: nowrap;
}

.action-btn {
  margin: 0 3px;
}

.accept-btn {
  color: #28a745;
}

.reject-btn {
  color: #dc3545;
}

.refer-btn {
  color: #007bff;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: $gap-sm;
}

/* Dialog styling */
.dialog-content {
  padding: 0 16px;
}

/* Centered dialog styling */
:deep(.centered-dialog) {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: $z-kendo-popup;
}

:deep(.k-overlay) {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: $z-kendo-overlay;
}

.consultant-select {
  margin-top: 16px;
}

.loading-consultants, .no-consultants {
  padding: $spacing-2 0;
  color: #6c757d;
}

.aoeLeads .action-buttons button {
  padding: $spacing-1;
}

.full-width-grid {
  width: 100%;
}

.full-width-grid table {
  width: 100%;
}

.full-width-grid .k-grid-header,
.full-width-grid .k-grid-container {
  width: 100% !important;
}
</style>
